/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { externalData, type ObservableValue } from '@amazon/vinyl-observable'

// The nav may unsubscribe while the gate's chunk is loading; subscribing anyway would leak.
function lazyGate(load: () => Promise<ObservableValue<boolean>>): ObservableValue<boolean> {
    return externalData<boolean>(false, (setData) => {
        let dispose: (() => void) | undefined
        let disposed = false
        load()
            .then((gate$) => {
                if (disposed) return
                dispose = gate$.onData(setData)
            })
            .catch(() => {
                // Chunk load failed — leave the gate fail-safe (nav hidden).
            })
        return () => {
            disposed = true
            dispose?.()
        }
    })
}

/**
 * Boot-safe bridges for the restricted-profile nav gates. The gates live in
 * lazily loaded modules that carry their profile probes; a static import from
 * the header (which IS on the first-paint chunk) would drag the probes back
 * onto boot. Each bridge imports its gate only when a nav subscribes, and
 * fails SAFE (nav hidden) until the gate resolves.
 */
export const canAccessPodcastsAndAudiobooks$: ObservableValue<boolean> = lazyGate(() =>
    import('@/auth/childProfile').then((m) => m.canAccessPodcastsAndAudiobooks$)
)

export const canAccessAudiobooks$: ObservableValue<boolean> = lazyGate(() =>
    import('@/audiobook/audiobooksAvailable').then((m) => m.canAccessAudiobooks$)
)

/**
 * Lazy bridge for the route guard's confirmed-child check — same boot-safe
 * shape as the nav gate above: `childProfile` (which carries the probe) is
 * imported only when a guarded route asks. A failed chunk load resolves
 * `false` (a non-child is never restricted), matching childProfile's fail-open
 * route-guard contract.
 */
export function loadIsChildProfile(): Promise<boolean> {
    return import('@/auth/childProfile').then((m) => m.loadIsChildProfile()).catch(() => false)
}
